import InputRecords from '@/components/common/InputRecords';
import { Alert, Button, Descriptions, Divider, Drawer, Space, Spin, Tag, Tooltip, Typography } from 'antd';
import { FilePdfOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import CopyButton from '@/components/common/CopyButton';
import { CnyHint } from '@/components/common/AmountWithCNY';
import PayeeDetailBody from '@/components/payees/PayeeDetailBody';
import FailureDetailView from '@/components/payouts/FailureDetailView';
import { payeeToClipboard } from '@/domain/payeeDetail';
import { detailNotice, type PayoutDetail } from '@/domain/payoutDetail';
import {
  APPLICATION_STATUS_COLOR,
  APPLICATION_STATUS_LABEL,
  FEE_PAYER_LABEL,
  RECEIPT_PLACEHOLDER,
  RECEIPT_PLACEHOLDER_HINT,
  SWIFT_CHARGE_LABEL,
  isTerminalPayout,
  payoutStatusView,
} from '@/domain/payouts';
import type { SwiftChargeOption } from '@/api/payouts';
import type { Payee } from '@/api/payees';

const { Text, Title } = Typography;

const fmt = (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm');

/**
 * 一笔钱的完整详情。打款申请页与打款记录页点开任意一条记录看到的是**同一个抽屉**。
 *
 * 为什么不是两个：申请与打款记录是同一件事的两半（§5.1），而它们分在两页里。
 * 原先在申请页点收款人姓名只弹出收款信息，想知道「这条申请后来打出去了没有、
 * 单号是多少、确认函拿到没有」就得记住短 id 跑到另一页再搜一遍；反过来在打款记录页
 * 想知道「这笔钱当初为什么要打、谁提的、备注写了什么」也一样。
 *
 * 🔴 **这个抽屉是只读的。** 它不放任何会动钱的按钮 —— 打款仍然只能从打款申请页
 * 那一行最右边按下去，走原来那套二次确认（§5.2）。详情页是人决定下一步做什么的地方，
 * 在这里放一个「打款」等于给重复付款开一条谁也没想过的新路径。
 *
 * 🔴 顶部提示的措辞见 `detailNotice`：`INITIATING` / `UNKNOWN` 一律说「结果未知」
 * 并明写不要重新发起，绝不写成「失败」。
 */
export default function PayoutDetailDrawer({
  detail,
  payee,
  payeeLoading,
  payeeError,
  onClose,
  onViewReceipt,
}: {
  detail: PayoutDetail | null;
  /** 收款人本地镜像。取不到时抽屉照样打开 —— 记录本身的信息已经在手上了。 */
  payee: Payee | null;
  payeeLoading: boolean;
  payeeError: string | null;
  onClose: () => void;
  /** 打开转账确认函预览。不传就不渲染那个按钮。 */
  onViewReceipt?: (payoutId: string) => void;
}) {
  if (!detail) return null;

  const { application, payout } = detail;
  const notice = detailNotice(payout);
  const statusTag = payout
    ? payoutStatusView(payout)
    : application.status
      ? {
          label: APPLICATION_STATUS_LABEL[application.status],
          color: APPLICATION_STATUS_COLOR[application.status],
        }
      : null;

  return (
    <Drawer
      open
      onClose={onClose}
      size={560}
      title={
        <Space size={8} wrap>
          <span>{detail.payeeName ?? '收款人'}</span>
          <Tag>{detail.payeeShortId}</Tag>
          {detail.payeeNickname && (
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
              {detail.payeeNickname}
            </Text>
          )}
        </Space>
      }
      extra={
        payee && (
          <CopyButton
            text={payeeToClipboard(payee)}
            label="复制收款信息"
            type="default"
            successMessage="收款信息已复制"
          />
        )
      }
    >
      {notice && (
        <Alert
          style={{ marginBottom: 16 }}
          type={notice.type}
          showIcon
          title={notice.title}
          description={notice.description}
        />
      )}

      {/* 金额放在最上面、最大：看一笔记录的人第一眼要确认的就是「多少钱、现在在哪」 */}
      <Space direction="vertical" size={2} style={{ marginBottom: 16 }}>
        <Text strong style={{ fontSize: 22, fontVariantNumeric: 'tabular-nums' }}>
          {detail.amount} {detail.currency}
        </Text>
        <CnyHint amount={detail.amount} currency={detail.currency} />
        {statusTag && <Tag color={statusTag.color}>{statusTag.label}</Tag>}
      </Space>

      <Title level={5}>打款申请</Title>
      <div style={{ marginBottom: 12 }}><InputRecords key={application.id} applicationId={application.id} /></div>
      <Descriptions column={1} size="small" bordered>
        {application.status && (
          <Descriptions.Item label="申请状态">
            <Tag color={APPLICATION_STATUS_COLOR[application.status]}>
              {APPLICATION_STATUS_LABEL[application.status]}
            </Tag>
          </Descriptions.Item>
        )}
        <Descriptions.Item label="银行附言">{application.reference}</Descriptions.Item>
        <Descriptions.Item label="内部备注">
          {application.note ? (
            <Text style={{ whiteSpace: 'pre-wrap' }}>{application.note}</Text>
          ) : (
            '—'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="手续费承担方">
          {application.feePaidBy ? (
            <Space direction="vertical" size={0}>
              <Text>
                {FEE_PAYER_LABEL[application.feePaidBy]}
                {application.swiftChargeOption
                  ? ` · SWIFT 中转行 ${SWIFT_CHARGE_LABEL[application.swiftChargeOption as SwiftChargeOption]}`
                  : ''}
              </Text>
              {/* 🔴 「对方承担」意味着对方到手少于申请金额，这是一条业务不变式（§5.8），
                  在任何显示这个决定的地方都要跟着说一遍 */}
              {application.feePaidBy === 'BENEFICIARY' && (
                <Text type="warning" style={{ fontSize: 12 }}>
                  对方实际到手少于此金额。
                </Text>
              )}
            </Space>
          ) : (
            '—'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="申请人">{application.createdByName ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="申请时间">{fmt(application.createdAt)}</Descriptions.Item>
        {application.rejectionReason && (
          <Descriptions.Item label="拒绝理由">
            <Space direction="vertical" size={0}>
              <Text type="danger" style={{ whiteSpace: 'pre-wrap' }}>
                {application.rejectionReason}
              </Text>
              {application.rejectedByName && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {application.rejectedByName} 拒绝
                </Text>
              )}
            </Space>
          </Descriptions.Item>
        )}
      </Descriptions>

      <Title level={5} style={{ marginTop: 24 }}>
        打款记录
      </Title>
      {payout ? (
        <>
          <Descriptions column={1} size="small" bordered>
            <Descriptions.Item label="状态">
              <Space direction="vertical" size={0}>
                <Tag color={payoutStatusView(payout).color}>{payoutStatusView(payout).label}</Tag>
                {payout.providerStatus && payout.providerStatus !== payout.status && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Airwallex: {payout.providerStatus}
                  </Text>
                )}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Airwallex 单号">
              {payout.shortReferenceId ? (
                <Space size={4}>
                  <Text code style={{ fontSize: 12 }}>
                    {payout.shortReferenceId}
                  </Text>
                  <CopyButton text={payout.shortReferenceId} tooltip="复制单号" />
                </Space>
              ) : (
                '—'
              )}
            </Descriptions.Item>
            <Descriptions.Item label="执行人">{payout.executedByName ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="发起时间">{fmt(payout.createdAt)}</Descriptions.Item>
            {payout.paidAt && (
              <Descriptions.Item label="到账时间">{fmt(payout.paidAt)}</Descriptions.Item>
            )}
            <Descriptions.Item label="实际费用">
              {/* 🔴 上游还没回费用时写「上游还没给出」，不写 0 —— 0 是「确实不收费」，
                  两者显示成一样会让人以为已经有确定结果（§5.8.2） */}
              {payout.fee.amount === null ? (
                <Text type="secondary">上游还没给出</Text>
              ) : (
                <Space direction="vertical" size={0}>
                  <Text>手续费 {payout.fee.amount}</Text>
                  {payout.fee.beneficiaryReceives && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      对方到手 {payout.fee.beneficiaryReceives}
                    </Text>
                  )}
                  {application.swiftChargeOption === 'SHARED' && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      中转行还会再扣
                    </Text>
                  )}
                </Space>
              )}
            </Descriptions.Item>
            {payout.failureReason && (
              <Descriptions.Item label="失败原因">
                <Text type="danger" style={{ whiteSpace: 'pre-wrap' }}>
                  {payout.failureReason}
                </Text>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="转账确认函">
              <Space direction="vertical" size={0}>
                {payout.receipt === 'ARCHIVED' ? (
                  onViewReceipt ? (
                    <Button
                      type="link"
                      size="small"
                      style={{ paddingInline: 0, height: 'auto' }}
                      icon={<FilePdfOutlined />}
                      onClick={() => onViewReceipt(payout.id)}
                    >
                      查看
                    </Button>
                  ) : (
                    <Text>已归档</Text>
                  )
                ) : (
                  <Tooltip title={RECEIPT_PLACEHOLDER_HINT[payout.receipt]}>
                    <Text type="secondary">{RECEIPT_PLACEHOLDER[payout.receipt]}</Text>
                  </Tooltip>
                )}
                {/* 🔴 未到终态时必须写明：确认函证明的是 Airwallex 受理了这笔转账指令，
                    不是钱到了对方账上（§5.6）。 */}
                {payout.receipt === 'ARCHIVED' && !isTerminalPayout(payout.status) && (
                  <Text type="warning" style={{ fontSize: 12 }}>
                    不代表已到账，钱到没到以上面的状态为准
                  </Text>
                )}
              </Space>
            </Descriptions.Item>
          </Descriptions>

          {payout.failureDetail && (
            <>
              <Title level={5} style={{ marginTop: 24 }}>
                上游返回的失败原文
              </Title>
              <FailureDetailView
                detail={payout.failureDetail}
                payoutId={payout.id}
                shortReferenceId={payout.shortReferenceId}
              />
            </>
          )}
        </>
      ) : (
        <Text type="secondary">这条申请还没有执行打款，钱没有动过。</Text>
      )}

      <Divider />
      <Title level={5}>收款人</Title>
      {payeeLoading ? (
        <Spin />
      ) : payee ? (
        <PayeeDetailBody payee={payee} />
      ) : (
        /* 🔴 明说是「没取到」而不是留白：一片空白会让人以为这个收款人压根没填银行信息，
           而真相是我们没拿到 —— 对着一笔要打的钱，这两件事不能长得一样。 */
        <Alert
          type="warning"
          showIcon
          title="收款信息没能取到"
          description={`${payeeError ?? '请刷新后重试'}。这不代表这个收款人没有填收款信息。`}
        />
      )}
    </Drawer>
  );
}
